import type { RecordsTableFieldProperty } from '@cims-online/shared'
import type { ReactNode } from 'react'
import { CIMSTags } from 'components/CIMSTags'
import type { CIMSTagsTag } from 'components/CIMSTags/types'
import SingleUser from 'components/SingleUser'
import { useProjectContext } from 'context/ProjectContext'
import { dateFormatter } from 'utils/dateFormatter'
import type { WorkflowFieldValue } from '../../types'

function toTags(fieldValue: WorkflowFieldValue): CIMSTagsTag[] {
  if (fieldValue.type === 'select')
    return fieldValue.value.map(({ deleted, label, tooltip }) => ({
      text: label,
      tooltip,
      strikethrough: deleted,
    }))
  else if (fieldValue.type === 'approvals')
    return fieldValue.value
      .filter(({ optionDeleted }) => !optionDeleted)
      .map(({ optionLabel, optionTooltip }) => ({
        text: optionLabel,
        tooltip: optionTooltip,
      }))
  else if (fieldValue.type === 'selected_by')
    return fieldValue.value
      ? [
          {
            text: fieldValue.value.optionLabel,
            tooltip: fieldValue.value.optionTooltip,
            strikethrough: fieldValue.value.optionDeleted,
          },
        ]
      : []
  else if (fieldValue.type === 'pay_items')
    return fieldValue.value.map(
      ({ payItemDeleted, payItemLabel }): CIMSTagsTag => ({
        text: payItemLabel,
        strikethrough: payItemDeleted,
      })
    )
  return []
}

function SelectedByUser({ fieldValue }: { fieldValue: WorkflowFieldValue }) {
  const { userGroups } = useProjectContext()
  if (fieldValue.type !== 'selected_by' || !fieldValue.value) return null
  const {
    selectedByDeleted: deleted,
    selectedById: id,
    selectedByMail: mail,
    selectedByUsername: displayName,
  } = fieldValue.value
  return (
    <SingleUser
      deleted={deleted}
      displayName={displayName}
      id={id}
      mail={mail}
      userGroups={userGroups
        .filter(({ userIds }) => userIds.includes(id))
        .map(({ id }) => ({ id }))}
    />
  )
}

/**
 * Normalizes a transformed field value into searchable and sortable text.
 *
 * Scalar fields stringify directly. Fields whose `value` is an array or object
 * use the same labels rendered in the table, keeping behavior aligned with what
 * the user sees.
 */
export function fieldValueText(
  fieldValue: WorkflowFieldValue | undefined,
  property: RecordsTableFieldProperty
): string {
  if (!fieldValue) return ''
  if (property === 'formattedValue')
    return fieldValue.type === 'generated_int' ? fieldValue.formattedValue : ''
  else if (property === 'optionLabel')
    return fieldValue.type === 'selected_by'
      ? fieldValue.value?.optionLabel ?? ''
      : ''
  else if (property === 'selectedByUsername')
    return fieldValue.type === 'selected_by'
      ? fieldValue.value?.selectedByUsername ?? ''
      : ''
  else if (property === 'selectedDate')
    return fieldValue.type === 'selected_by'
      ? dateFormatter.simple(fieldValue.value?.selectedDate)
      : ''

  if (fieldValue.type === 'long_text') return fieldValue.value ?? ''
  else if (fieldValue.type === 'generated_int') return String(fieldValue.value)
  else if (fieldValue.type === 'comments')
    return fieldValue.value.map(({ text }) => text).join(' ')
  return toTags(fieldValue)
    .map(({ text }) => text)
    .join(' ')
}

/**
 * Renders the selected property of a transformed workflow field.
 *
 * Select-like values contain details objects that React cannot render
 * directly, so they become the same CIMS tags used by nearby tables.
 */
export function renderFieldValue(
  fieldValue: WorkflowFieldValue | undefined,
  property: RecordsTableFieldProperty
): ReactNode {
  if (!fieldValue) return ''
  if (property === 'selectedByUsername')
    return <SelectedByUser fieldValue={fieldValue} />
  if (
    property === 'value' &&
    (fieldValue.type === 'select' ||
      fieldValue.type === 'approvals' ||
      fieldValue.type === 'selected_by' ||
      fieldValue.type === 'pay_items')
  )
    return (
      <CIMSTags
        direction={
          fieldValue.type === 'approvals' ? { tags: 'vertical' } : undefined
        }
        tags={toTags(fieldValue)}
      />
    )
  return fieldValueText(fieldValue, property)
}
